import React, { useEffect, useState, useCallback, useRef } from 'react';

const TYPE_COLORS = { Incident: '#7c3aed', 'Sales Order Plan': '#0891b2', 'Project Plan': '#059669', Unknown: '#94a3b8' };
const getTypeColor = (t) => TYPE_COLORS[t] || '#6366f1';

/* Stable per-person identity colour — keyed by the person's name, not a list
   index, so someone keeps the same colour across every widget and regardless of
   roster order or the team-only filter. DESIGN.md: identity colour is data. */
const TEAM_PALETTE = ['#2563eb', '#1a80a0', '#7c3aed', '#1a8f4e', '#bf7c1a', '#c42028', '#0f766e', '#9333ea'];
function personColor(name) {
  if (!name) return 'var(--text-2)';
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return TEAM_PALETTE[Math.abs(h) % TEAM_PALETTE.length];
}

const initialsOf = (m) =>
  m.initials || (m.name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

/* Make a non-button element keyboard-operable: Enter / Space activate it. */
const clickable = (fn) => ({
  role: 'button',
  tabIndex: 0,
  onClick: fn,
  onKeyDown: (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); }
  },
});

/* Move focus into a drawer on open and restore it to the trigger on close. */
function useDrawerFocus(ref) {
  useEffect(() => {
    const prev = document.activeElement;
    ref.current?.focus();
    return () => { try { prev?.focus?.(); } catch { /* trigger gone */ } };
  }, [ref]);
}

function priorityBadge(p) {
  const cls = { Critical: 'badge-critical', Highest: 'badge-critical', High: 'badge-high', Medium: 'badge-medium', Low: 'badge-low', Lowest: 'badge-low' }[p] || 'badge-default';
  return <span className={`badge ${cls}`}>{p || '—'}</span>;
}

function IncidentBadge({ icon, label, count, tone = 'neutral', sub, onClick }) {
  const color = tone === 'warn' ? 'var(--warn)' : tone === 'ok' ? 'var(--ok)' : 'var(--text)';
  return (
    <button type="button" className="card stat-badge-card" onClick={onClick}
      aria-label={`${label}: ${count}${sub ? '. ' + sub : ''}. Open list`}>
      <div className="stat-badge-icon" style={{ background: 'var(--surface-2)', color }}>
        <span aria-hidden="true">{icon}</span>
      </div>
      <div className="stat-badge-flex">
        <div className="stat-badge-label">{label}</div>
        <div className="stat-badge-count mono" style={{ color }}>{count}</div>
        {sub && <div className="stat-badge-sub">{sub}</div>}
      </div>
      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"
        className="stat-badge-chevron" style={{ color: 'var(--text-3)', opacity: 0.8 }} aria-hidden="true">
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  );
}

function TicketDrawer({ api, uploadId, filterLabel, filterParam, filterValue, onClose, globalJiraUrl, teamOnly, closedMonth }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sortBy, setSortBy] = useState('age');
  const [sortDir, setSortDir] = useState('desc');
  const [search, setSearch] = useState('');
  const jiraUrl = globalJiraUrl || localStorage.getItem('jiraUrl') || '';
  const drawerRef = useRef(null);
  useDrawerFocus(drawerRef);

  const load = useCallback(() => {
    setLoading(true); setError(false);
    let url;
    if (closedMonth) {
      const params = new URLSearchParams();
      if (filterParam === 'project') params.set('project', filterValue);
      url = `${api}/api/uploads/${uploadId}/incidents-closed-month?${params}`;
    } else {
      const params = new URLSearchParams({ [filterParam]: filterValue });
      if (jiraUrl) params.set('jira_url', jiraUrl);
      params.set('team_only', !!teamOnly);
      url = `${api}/api/uploads/${uploadId}/tickets?${params}`;
    }
    fetch(url)
      .then(r => { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
      .then(d => { setTickets(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [api, uploadId, filterParam, filterValue, jiraUrl, closedMonth, teamOnly]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const dir = sortDir === 'desc' ? -1 : 1;
  const PRIORITY_ORDER_DRAWER = ['Critical', 'Highest', 'High', 'Medium', 'Low', 'Lowest'];
  let sorted = [...tickets];
  if (sortBy === 'age') sorted.sort((a, b) => (b.age_days - a.age_days) * dir);
  if (sortBy === 'key') sorted.sort((a, b) => a.key.localeCompare(b.key) * dir);
  if (sortBy === 'assignee') sorted.sort((a, b) => (a.assignee || '').localeCompare(b.assignee || '') * dir);
  if (sortBy === 'priority') sorted.sort((a, b) => (PRIORITY_ORDER_DRAWER.indexOf(a.priority) - PRIORITY_ORDER_DRAWER.indexOf(b.priority)) * dir);

  const filtered = search
    ? sorted.filter(t => t.key.toLowerCase().includes(search.toLowerCase()) ||
        t.summary.toLowerCase().includes(search.toLowerCase()) ||
        (t.assignee || '').toLowerCase().includes(search.toLowerCase()))
    : sorted;

  return (
    <>
      <div onClick={onClose} className="drawer-overlay" />
      <div className="drawer drawer--md" role="dialog" aria-modal="true"
        aria-label={`${filterLabel}: ${filterValue}`} tabIndex={-1} ref={drawerRef}>
        <div className="drawer-header">
          <div>
            <div className="drawer-title">
              <span className="badge badge-blue drawer-count-badge">{tickets.length}</span>
              {filterLabel}: {filterValue}
            </div>
            <div className="drawer-subtitle">
              {teamOnly ? 'Team only' : 'All assignees'} · {tickets.length} in this snapshot
            </div>
          </div>
          <button onClick={onClose} className="drawer-close-btn" aria-label="Close">
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="drawer-toolbar">
          <input type="text" placeholder="Search key, summary, or assignee…" value={search} onChange={e => setSearch(e.target.value)}
            className="drawer-search" aria-label="Search tickets" />
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="drawer-select" aria-label="Sort by">
            <option value="age">Age</option>
            <option value="key">Key</option>
            <option value="assignee">Assignee</option>
            <option value="priority">Priority</option>
          </select>
          <button onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')} className="drawer-sort-btn"
            aria-label={sortDir === 'desc' ? 'Sort ascending' : 'Sort descending'}>
            {sortDir === 'desc' ? '↓' : '↑'}
          </button>
        </div>
        <div className="drawer-body">
          {loading ? <div className="loading">Loading…</div>
            : error ? <div className="empty"><p>Couldn’t load tickets — check the connection and try reopening.</p></div>
            : filtered.length === 0 ? <div className="empty"><p>No tickets match.</p></div>
            : filtered.map((t, idx) => (
              <div key={t.key} className="drawer-ticket-row">
                <span className="drawer-ticket-idx">{idx + 1}</span>
                <div className="drawer-ticket-main">
                  <div className="drawer-ticket-meta">
                    {t.url
                      ? <a href={t.url} target="_blank" rel="noopener noreferrer" className="ticket-key-link">{t.key} ↗</a>
                      : <span className="badge badge-blue badge-key-sm">{t.key}</span>}
                    <span className="ticket-type-pill" style={{ background: getTypeColor(t.issue_type) + '22', color: getTypeColor(t.issue_type) }}>{t.issue_type}</span>
                    {priorityBadge(t.priority)}
                    {t.urgency && <span className="ticket-urgency-pill">⚡ {t.urgency}</span>}
                    {t.impact && <span className="ticket-impact-pill">💥 {t.impact}</span>}
                    <span className="ticket-assignee-text">{t.assignee || 'Unassigned'}</span>
                  </div>
                  <div className="drawer-ticket-summary">{t.summary}</div>
                </div>
                <div className="drawer-ticket-age">
                  <div className="drawer-ticket-age-val" style={{ color: t.age_days > 90 ? 'var(--critical)' : t.age_days > 30 ? 'var(--warn)' : 'var(--text)' }}>
                    {t.age_days > 90 ? '● ' : t.age_days > 30 ? '▲ ' : ''}{t.age_days}d
                  </div>
                  <div className="drawer-ticket-logged">{t.time_spent || '—'} logged</div>
                </div>
              </div>
            ))}
        </div>
        <div className="drawer-footer">
          <span>Showing {filtered.length} of {tickets.length} tickets</span>
          <span><span className="drawer-legend-warn">▲</span> &gt;30d &nbsp;<span className="drawer-legend-crit">●</span> &gt;90d</span>
        </div>
      </div>
    </>
  );
}

function AssigneeDrawer({ api, uploadId, assignee, onClose, date, jiraUrl: propJiraUrl }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const jiraUrl = propJiraUrl || localStorage.getItem('jiraUrl') || '';
  const drawerRef = useRef(null);
  useDrawerFocus(drawerRef);

  React.useEffect(() => {
    setLoading(true); setError(null);
    const _params = new URLSearchParams();
    if (jiraUrl) _params.set('jira_url', jiraUrl);
    if (date) _params.set('date', date);
    const _qs = _params.toString() ? '?' + _params.toString() : '';
    fetch(`${api}/api/jira/worklogs/today/member/${encodeURIComponent(assignee)}${_qs}`)
      .then(r => { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [api, assignee, date, jiraUrl]);

  React.useEffect(() => {
    const h = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return (
    <>
      <div onClick={onClose} className="drawer-overlay" />
      <div className="drawer drawer--lg" role="dialog" aria-modal="true"
        aria-label={`Time logged — ${assignee}`} tabIndex={-1} ref={drawerRef}>
        <div className="drawer-header drawer-header--gap">
          <div className="assignee-avatar" style={{ background: personColor(assignee) + '22', color: personColor(assignee) }}>
            {assignee.split(' ').map(w => w[0]).slice(0,2).join('').toUpperCase()}
          </div>
          <div className="flex-1">
            <div className="assignee-name">{assignee}</div>
            {data && <div className="assignee-logged">Time logged: <strong>{data.total_display || '—'}</strong></div>}
          </div>
          <button onClick={onClose} className="drawer-close-btn" aria-label="Close">
            <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div className="drawer-body drawer-body-pad">
          {loading && <div className="loading">Loading worklogs…</div>}
          {error && <div className="error-text-pad">Couldn’t load worklogs — {error}</div>}
          {!loading && !error && data && (
            <div>
              <div className="drawer-section-info">
                {data.entries?.length || 0} worklog entries · Total: <strong>{data.total_display || '—'}</strong>
              </div>
              {(!data.entries || data.entries.length === 0) && (
                <div className="empty"><p>No time logged for {assignee} in this period.</p></div>
              )}
              {data.entries?.map((e, idx) => (
                <div key={idx} className="drawer-worklog-row">
                  <div className="drawer-worklog-main">
                    <div className="drawer-worklog-meta">
                      {e.issue_key && ((e.url || jiraUrl)
                        ? <a href={e.url || `${jiraUrl}/browse/${e.issue_key}`} target="_blank" rel="noopener noreferrer" className="worklog-key-link">{e.issue_key} ↗</a>
                        : <span className="worklog-key-link">{e.issue_key}</span>
                      )}
                      {e.started_time && <span className="worklog-time-badge">🕐 {e.started_time}</span>}
                    </div>
                    <div className="drawer-worklog-comment">{e.comment || e.issue_summary || e.issue_key}</div>
                  </div>
                  <div className="drawer-worklog-duration">{e.display}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function fmtHM(seconds) {
  if (!seconds) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

/* One time instrument. "Day" hits /worklogs/today (with an optional ?date=),
   "Month" hits /worklogs/monthly. Replaces the old TeamTimeWidget +
   MonthlyTimeWidget twins. Drill-in to a person is available in Day mode
   (the member worklog endpoint is day-scoped). */
function TimeLoggedWidget({ api, uploadId, jiraUrl }) {
  const [mode, setMode] = React.useState('today');      // 'today' | 'month'
  const [offset, setOffset] = React.useState(0);        // 0 = current, negative = past
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [expanded, setExpanded] = React.useState(true);
  const [selected, setSelected] = React.useState(null);

  const localDateStr = (d) =>
    [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');

  const dayStr = React.useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + offset); return localDateStr(d);
  }, [offset]);

  const monthStr = React.useMemo(() => {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() + offset);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;  // local — no toISOString
  }, [offset]);

  React.useEffect(() => {
    setLoading(true); setError(null);
    const url = mode === 'today'
      ? `${api}/api/jira/worklogs/today${offset === 0 ? '' : `?date=${dayStr}`}`
      : `${api}/api/jira/worklogs/monthly?month=${monthStr}`;
    fetch(url)
      .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [api, mode, offset, dayStr, monthStr]);

  const switchMode = (m) => { if (m === mode) return; setMode(m); setOffset(0); setSelected(null); };
  const navigate = (dir) => { const n = offset + dir; if (n > 0) return; setOffset(n); setSelected(null); };

  // Jump straight to a date instead of stepping back a day at a time. The
  // widget works in offsets from today, so the picked date becomes an offset;
  // midday avoids a DST jump turning the difference into 23 or 25 hours.
  const todayStr = localDateStr(new Date());
  const pickDay = (value) => {
    if (!value) return;
    const days = Math.round((new Date(`${value}T12:00:00`) - new Date(`${todayStr}T12:00:00`)) / 86400000);
    setOffset(Math.min(days, 0));
    setSelected(null);
  };

  const dayLabel = () => {
    if (offset === 0) return 'Today';
    if (offset === -1) return 'Yesterday';
    return new Date(dayStr + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };
  const label = mode === 'today' ? dayLabel() : (data?.label ?? '…');

  const members = data?.members || [];
  const totalDisplay = mode === 'today' ? fmtHM(data?.total_seconds) : (data?.total_display ?? '—');
  const maxSec = mode === 'today' ? (8 * 3600) : (members[0]?.seconds || 1);
  const memberTime = (m) => mode === 'today' ? (m.seconds > 0 ? fmtHM(m.seconds) : '—') : (m.display || '—');

  return (
    <div className="card-spaced-lg">
      <div className="card">
        <div className="widget-header">
          <div className="widget-header-main">
            <div className="section-title section-title--inline">Time Logged</div>
            <div className="nav-controls" role="group" aria-label="Time range">
              <button type="button" onClick={() => switchMode('today')} aria-pressed={mode === 'today'}
                className={`seg-btn${mode === 'today' ? ' seg-btn--active' : ''}`}>Day</button>
              <button type="button" onClick={() => switchMode('month')} aria-pressed={mode === 'month'}
                className={`seg-btn${mode === 'month' ? ' seg-btn--active' : ''}`}>Month</button>
            </div>
            <div className="nav-controls">
              <button onClick={() => navigate(-1)} className="nav-btn" aria-label="Previous">&#8249;</button>
              <span className={offset === 0 ? 'nav-label nav-label--today' : 'nav-label nav-label--past'}>{label}</span>
              <button onClick={() => navigate(1)} disabled={offset >= 0} className="nav-btn" aria-label="Next">&#8250;</button>
              {mode === 'today' && (
                <input type="date" className="input" value={dayStr} max={todayStr}
                  onChange={e => pickDay(e.target.value)} aria-label="Pick a day"
                  style={{ fontSize: 12, padding: '3px 7px', width: 140, marginLeft: 4 }} />
              )}
            </div>
            <span className="team-total-time">{totalDisplay}</span>
          </div>
          <button onClick={() => setExpanded(e => !e)} className="widget-toggle-btn" aria-expanded={expanded}>
            {expanded ? 'Hide' : 'Details'}
          </button>
        </div>

        {loading && <div className="loading-text">Loading…</div>}
        {error && <div className="error-text">Couldn’t load time logs — {error}</div>}
        {!loading && !error && members.length === 0 && (
          <div className="italic-muted">
            No time logged {mode === 'today' ? `on ${label.toLowerCase()}` : `in ${label}`}.
          </div>
        )}

        {!loading && !error && members.length > 0 && expanded && (
          <div className="member-card-grid">
            {members.map((m) => {
              const color = personColor(m.name);
              const pct = Math.min((m.seconds / maxSec) * 100, 100);
              const drill = mode === 'today' ? clickable(() => setSelected(m.name)) : {};
              return (
                <div key={m.name} className="member-card" {...drill}
                  aria-label={mode === 'today' ? `${m.name}: ${memberTime(m)}. Open worklog` : `${m.name}: ${memberTime(m)}`}
                  style={{ background: color + '0d', borderColor: color + '22', cursor: mode === 'today' ? 'pointer' : 'default' }}
                  onMouseEnter={e => { if (mode === 'today') e.currentTarget.style.background = color + '1f'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = color + '0d'; }}>
                  <div className="member-card-header">
                    <div className="member-avatar" style={{ background: color }}>{initialsOf(m)}</div>
                    <div className="min-w-0">
                      <div className="member-name-sm">{m.name}</div>
                      <div className="member-time-lg" style={{ color: m.seconds > 0 ? color : 'var(--text-2)' }}>{memberTime(m)}</div>
                    </div>
                  </div>
                  <div className="bar-bg">
                    <div className="bar-fill" style={{ width: `${pct}%`, background: color }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && !error && members.length > 0 && !expanded && (
          <div className="mini-pills-row">
            {members.map((m) => {
              const color = personColor(m.name);
              const drill = mode === 'today' ? clickable(() => setSelected(m.name)) : {};
              return (
                <div key={m.name} className="member-mini-pill" {...drill}
                  title={`${m.name}: ${memberTime(m)}`} aria-label={`${m.name}: ${memberTime(m)}`}
                  style={{ background: color + '18', borderColor: color + '33', cursor: mode === 'today' ? 'pointer' : 'default' }}>
                  <div className="member-mini-avatar" style={{ background: color }}>{initialsOf(m)}</div>
                  <span className="member-mini-time" style={{ color: m.seconds > 0 ? color : 'var(--text-2)' }}>{memberTime(m)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selected && uploadId && mode === 'today' && (
        <AssigneeDrawer api={api} uploadId={uploadId} assignee={selected}
          date={offset === 0 ? null : dayStr} jiraUrl={jiraUrl}
          onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function PlannedTodayWidget({ api }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [expanded, setExpanded] = React.useState({});

  const load = React.useCallback(() => {
    setLoading(true); setError(null);
    fetch(`${api}/api/jira/planned/today`)
      .then(r => { if (!r.ok) throw new Error("HTTP " + r.status); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [api]);

  React.useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="card card-spaced">
      <div className="loading-text">Loading planned tasks…</div>
    </div>
  );
  if (error || !data || !Array.isArray(data.members)) return null;

  const toggle = (name) => setExpanded(e => ({ ...e, [name]: !e[name] }));
  const activeMembers = data.members.filter(m => m.count > 0);

  return (
    <div className="card card-spaced">
      <div className="widget-header">
        <div className="section-title section-title--inline">Tasks Planned Today</div>
        <div className="widget-header-right">
          <span className="planned-total-count">{data.total} task{data.total !== 1 ? 's' : ''}</span>
          <button onClick={load} title="Refresh" aria-label="Refresh planned tasks" className="widget-toggle-btn">↻</button>
        </div>
      </div>
      {activeMembers.length === 0 && (
        <div className="planned-no-tasks">No tasks planned today for any team member.</div>
      )}
      {activeMembers.map((member) => {
        const color = personColor(member.name);
        const isOpen = !!expanded[member.name];
        return (
          <div key={member.name} className="planned-member-item">
            <div {...clickable(() => toggle(member.name))} aria-expanded={isOpen}
              className="planned-member-row"
              style={{ background: color + '0d', borderColor: color + '22' }}
              onMouseEnter={e => e.currentTarget.style.background = color + '1a'}
              onMouseLeave={e => e.currentTarget.style.background = color + '0d'}>
              <div className="planned-avatar" style={{ background: color }}>{member.initials}</div>
              <span className="planned-member-name">{member.name}</span>
              <span className="planned-task-count" style={{ color: member.count > 0 ? color : 'var(--text-2)', background: color + '18' }}>
                {member.count} task{member.count !== 1 ? 's' : ''}
              </span>
              <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"
                className="planned-expand-icon"
                style={{ transform: isOpen ? 'rotate(90deg)' : '' }} aria-hidden="true">
                <polyline points="9 18 15 12 9 6"/>
              </svg>
            </div>
            {isOpen && member.tasks.map(task => (
              <div key={task.key}
                className="planned-task-row"
                style={{ background: 'var(--surface-2)', borderRadius: 7 }}
                onMouseEnter={e => e.currentTarget.style.background = color + '0d'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--surface-2)'}>
                <div className="planned-task-main">
                  <div className="planned-task-meta">
                    <a href={task.url} target="_blank" rel="noopener noreferrer" className="planned-task-key">{task.key} ↗</a>
                    {task.status && <span className="planned-task-status">{task.status}</span>}
                    {task.priority && priorityBadge(task.priority)}
                  </div>
                  <div className="planned-task-summary">{task.summary}</div>
                </div>
                {(task.planned_start || task.planned_end) && (
                  <div className="planned-task-time">
                    {task.planned_start && <div>{task.planned_start.length > 10 ? task.planned_start.substring(11) : task.planned_start}</div>}
                    {task.planned_end && <div>→ {task.planned_end.length > 10 ? task.planned_end.substring(11) : task.planned_end}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default function OverviewPage({ api, uploadId, jiraUrl: globalJiraUrl, teamOnly }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [incidentDrawer, setIncidentDrawer] = useState(null);

  useEffect(() => {
    if (!uploadId) { setData(null); return; }
    setLoading(true); setError(null);
    fetch(`${api}/api/uploads/${uploadId}/overview?team_only=${!!teamOnly}`)
      .then(r => { if (!r.ok) throw new Error('Failed'); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [uploadId, api, teamOnly]);

  if (!uploadId) return <div className="empty"><svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M9 17H7A5 5 0 0 1 7 7h2M15 7h2a5 5 0 0 1 0 10h-2M8 12h8"/></svg><p>Select or upload a Jira export to get started.</p></div>;
  if (loading) return <div className="loading">Loading overview…</div>;
  if (error) return <div className="empty"><p className="error-text">{error}</p></div>;
  if (!data) return null;

  const s = data.incident_stats || {};

  return (
    <div>
      <div className="stat-badge-grid">
        <IncidentBadge icon="🎫" label="Open Incidents" count={s.total ?? 0} tone="neutral" sub="All incident tickets" onClick={() => setIncidentDrawer('total')} />
        <IncidentBadge icon="👤" label="Unassigned" count={s.unassigned ?? 0} tone="warn" sub="Unresolved · no assignee" onClick={() => setIncidentDrawer('unassigned')} />
        <IncidentBadge icon="✅" label="Closed Today" count={s.closed_today ?? 0} tone="ok" sub="Incidents resolved today" onClick={() => setIncidentDrawer('closed_today')} />
        <IncidentBadge icon="📅" label="Closed This Week" count={s.closed_week ?? 0} tone="neutral" sub="Since Monday" onClick={() => setIncidentDrawer('closed_week')} />
      </div>

      <TimeLoggedWidget api={api} uploadId={uploadId} jiraUrl={globalJiraUrl} />

      <PlannedTodayWidget api={api} />

      {incidentDrawer && (
        <TicketDrawer api={api} uploadId={uploadId}
          filterLabel={
            incidentDrawer === 'total' ? 'Open Incidents' :
            incidentDrawer === 'unassigned' ? 'Unassigned Incidents' :
            incidentDrawer === 'closed_today' ? 'Incidents Closed Today' :
            incidentDrawer === 'closed_week' ? 'Incidents Closed This Week' : 'Incidents'
          }
          filterParam="incident_filter"
          filterValue={incidentDrawer}
          globalJiraUrl={globalJiraUrl} teamOnly={teamOnly} onClose={() => setIncidentDrawer(null)} />
      )}
    </div>
  );
}
